import { notify } from "../../../util/notify";
import { focusFirstError, readApiErrors } from "../../../util/formErrors";
import React, { useState, useEffect } from "react";
import { Link, useHistory } from "react-router-dom";
import axios from "axios";
import { imageurl } from "../../../App";
import { getWishlistCount } from "../../../util/wishlistService";
import { getProductImageUrl, handleProductImageError } from "../../../util/storeLogoHelper";
import { clearCustomerSession, isCustomerLoggedIn } from "../../../util/customerAuth";
import { loadStoreSettings } from "../../../util/storeSettings";
import {
  FaUser,
  FaShoppingBag,
  FaHeart,
  FaMapMarkerAlt,
  FaLock,
  FaTruck,
  FaCheckCircle,
  FaClock,
  FaPlus,
  FaTrashAlt,
  FaSignOutAlt,
  FaBoxOpen,
  FaPhoneAlt,
  FaFileInvoiceDollar
} from "react-icons/fa";

export default function AccountDashboard() {
  const history = useHistory();
  const [activeTab, setActiveTab] = useState("overview");
  const [storeSettings, setStoreSettings] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [addresses, setAddresses] = useState([]);
  const [loadingAddresses, setLoadingAddresses] = useState(true);
  const [showAddAddress, setShowAddAddress] = useState(false);
  const [savingAddress, setSavingAddress] = useState(false);
  // Problems shown under the fields instead of popups.
  const [addressErrors, setAddressErrors] = useState({});
  const [passwordErrors, setPasswordErrors] = useState({});
  const [updatingPassword, setUpdatingPassword] = useState(false);

  const [newAddress, setNewAddress] = useState({
    title: "Home",
    full_name: "",
    phone: "",
    address: "",
    city: "Karachi",
    state: "",
    zipcode: "",
    is_default: false,
  });

  const [passwordInput, setPasswordInput] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const authName = sessionStorage.getItem("auth_name") || localStorage.getItem("auth_name") || "Valued Customer";
  const authRole = sessionStorage.getItem("role") || localStorage.getItem("role") || "Customer";
  const isLoggedIn = isCustomerLoggedIn();

  const loadAddresses = () => {
    setLoadingAddresses(true);
    axios
      .get(`/api/user-addresses`)
      .then((res) => {
        if (res.data.status === 200) {
          setAddresses(res.data.addresses || []);
        }
        setLoadingAddresses(false);
      })
      .catch(() => {
        setLoadingAddresses(false);
      });
  };

  const loadOrders = () => {
    setLoadingOrders(true);
    axios
      .get(`/api/view-orders`)
      .then((res) => {
        if (res.data.status === 200) {
          setOrders(res.data.orders || []);
        }
        setLoadingOrders(false);
      })
      .catch(() => {
        setLoadingOrders(false);
      });
  };

  useEffect(() => {
    if (!isLoggedIn) {
      notify("Please sign in to see your account.", "info");
      history.push("/login");
      return;
    }

    window.scrollTo(0, 0);

    loadStoreSettings().then((settings) => {
      if (settings && (settings.storeName || settings.logoUrl)) {
        setStoreSettings(settings);
        document.title = `My Account Dashboard — ${settings.storeName || "Store"}`;
      }
    }).catch(() => {
      document.title = "My Account Dashboard — Store";
    });

    loadAddresses();
    loadOrders();
  }, [history, isLoggedIn]);

  const handleLogout = () => {
    axios.get("/sanctum/csrf-cookie").then(() => {
      axios.post(`/api/logout`).then(() => {
        clearCustomerSession();
        notify("You are signed out.");
        history.replace("/");
      }).catch(() => {
        clearCustomerSession();
        history.replace("/");
      });
    });
  };

  const handleSaveAddress = (e) => {
    e.preventDefault();
    const found = {};
    if (!String(newAddress.full_name || "").trim()) found.full_name = "Enter the contact person's name.";
    if (!String(newAddress.phone || "").trim()) found.phone = "Enter a phone number.";
    else if (String(newAddress.phone).replace(/\D/g, "").length < 10) found.phone = "Enter a full phone number, e.g. 0300-1234567.";
    if (!String(newAddress.address || "").trim()) found.address = "Enter the house, street and area.";
    setAddressErrors(found);
    if (Object.keys(found).length) {
      focusFirstError();
      return;
    }

    setSavingAddress(true);
    axios
      .post(`/api/user-addresses`, newAddress)
      .then((res) => {
        setSavingAddress(false);
        if (res.data.status === 200) {
          setShowAddAddress(false);
          setNewAddress({
            title: "Home",
            full_name: "",
            phone: "",
            address: "",
            city: "Karachi",
            state: "",
            zipcode: "",
            is_default: false,
          });
          loadAddresses();
          notify(res.data.message || "Address saved");
        } else {
          const { message, fields } = readApiErrors(res);
          setAddressErrors({ ...fields, form: message || "Could not save the address." });
        }
      })
      .catch((err) => {
        setSavingAddress(false);
        const { message, fields } = readApiErrors(err);
        setAddressErrors({ ...fields, form: message || "Could not save the address. Please try again." });
      });
  };

  const handleDeleteAddress = (id) => {
    axios.delete(`/api/user-addresses/${id}`).then((res) => {
      if (res.data.status === 200) {
        loadAddresses();
      }
    });
  };

  const handleChangePassword = (e) => {
    e.preventDefault();
    const found = {};
    if (!passwordInput.currentPassword) found.currentPassword = "Enter your current password.";
    if (!passwordInput.newPassword || passwordInput.newPassword.length < 8) found.newPassword = "Use at least 8 characters.";
    if (passwordInput.newPassword !== passwordInput.confirmPassword) found.confirmPassword = "This does not match the new password.";
    setPasswordErrors(found);
    if (Object.keys(found).length) {
      focusFirstError();
      return;
    }

    setUpdatingPassword(true);
    axios
      .post(`/api/change-password`, {
        currentPassword: passwordInput.currentPassword,
        newPassword: passwordInput.newPassword,
      })
      .then((res) => {
        setUpdatingPassword(false);
        if (res.data.status === 200) {
          notify(res.data.message || "Password updated");
          setPasswordInput({ currentPassword: "", newPassword: "", confirmPassword: "" });
        } else {
          const { message, fields } = readApiErrors(res);
          setPasswordErrors({ ...fields, form: message || "Could not update the password." });
        }
      })
      .catch((err) => {
        setUpdatingPassword(false);
        const { message, fields } = readApiErrors(err);
        setPasswordErrors({ ...fields, form: message || "Could not update the password. Please try again." });
      });
  };

  const wishlistCount = getWishlistCount();

  // Helper to calculate total amount for order
  const getOrderTotal = (ord) => {
    if (ord.total_amount && ord.total_amount > 0) {
      return ord.total_amount;
    }
    let sum = 0;
    if (ord.orderitems && ord.orderitems.length > 0) {
      ord.orderitems.forEach((item) => {
        const p = item.price || item.productsprice?.seeling_price || 0;
        const q = item.qty || 1;
        sum += p * q;
      });
    }
    return sum;
  };

  return (
    <div>
      {/* Header Banner & Breadcrumbs */}
      <div className="page-header-modern">
        <div className="container">
          <ul className="breadcrumb-modern">
            <li>
              <Link to="/">Home</Link>
            </li>
            <li className="breadcrumb-separator">/</li>
            <li className="active">Account Dashboard</li>
          </ul>
          <h1 className="page-header-title">Welcome, {authName}</h1>
        </div>
      </div>

      <div className="container py-4">
        <div className="row g-4">
          {/* Left: Account Navigation Sidebar */}
          <div className="col-12 col-lg-3">
            <div className="cart-table-card p-3">
              <div className="d-flex align-items-center gap-3 p-3 mb-3 bg-light rounded-3">
                <div
                  style={{
                    width: "46px",
                    height: "46px",
                    borderRadius: "50%",
                    background: "var(--primary)",
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "18px",
                  }}
                >
                  <FaUser />
                </div>
                <div>
                  <h6 className="fw-bold mb-0">{authName}</h6>
                  <span className="badge bg-secondary text-uppercase" style={{ fontSize: "11px" }}>
                    {authRole}
                  </span>
                </div>
              </div>

              <div className="d-flex flex-column gap-1">
                <button
                  type="button"
                  onClick={() => setActiveTab("overview")}
                  className={`btn text-start d-flex align-items-center gap-2 p-2 rounded-2 ${
                    activeTab === "overview" ? "btn-primary" : "btn-light"
                  }`}
                  style={{ fontSize: "13.5px", fontWeight: 600 }}
                >
                  <FaUser size={13} /> Account Overview
                </button>

                <button
                  type="button"
                  onClick={() => setActiveTab("orders")}
                  className={`btn text-start d-flex align-items-center gap-2 p-2 rounded-2 ${
                    activeTab === "orders" ? "btn-primary" : "btn-light"
                  }`}
                  style={{ fontSize: "13.5px", fontWeight: 600 }}
                >
                  <FaShoppingBag size={13} /> Order History ({orders.length})
                </button>

                <button
                  type="button"
                  onClick={() => setActiveTab("addresses")}
                  className={`btn text-start d-flex align-items-center gap-2 p-2 rounded-2 ${
                    activeTab === "addresses" ? "btn-primary" : "btn-light"
                  }`}
                  style={{ fontSize: "13.5px", fontWeight: 600 }}
                >
                  <FaMapMarkerAlt size={13} /> Saved Addresses ({addresses.length})
                </button>

                <Link
                  to="/wishlist"
                  className="btn btn-light text-start d-flex align-items-center gap-2 p-2 rounded-2"
                  style={{ fontSize: "13.5px", fontWeight: 600 }}
                >
                  <FaHeart size={13} color="var(--danger)" /> My Wishlist ({wishlistCount})
                </Link>

                <button
                  type="button"
                  onClick={() => setActiveTab("settings")}
                  className={`btn text-start d-flex align-items-center gap-2 p-2 rounded-2 ${
                    activeTab === "settings" ? "btn-primary" : "btn-light"
                  }`}
                  style={{ fontSize: "13.5px", fontWeight: 600 }}
                >
                  <FaLock size={13} /> Password & Security
                </button>

                <hr className="my-2" />

                <button
                  type="button"
                  onClick={handleLogout}
                  className="btn btn-outline-danger btn-sm text-start d-flex align-items-center gap-2 p-2 rounded-2"
                >
                  <FaSignOutAlt size={13} /> Sign Out
                </button>
              </div>
            </div>
          </div>

          {/* Right: Tab Content */}
          <div className="col-12 col-lg-9">
            {/* 1. Overview Tab */}
            {activeTab === "overview" && (
              <div>
                {/* Stats Cards */}
                <div className="row g-3 mb-4">
                  <div className="col-12 col-md-4">
                    <div className="p-4 bg-white rounded-3 border d-flex align-items-center gap-3" style={{ boxShadow: "var(--shadow-xs)" }}>
                      <div className="trust-feature-icon" style={{ background: "var(--accent-light)", color: "var(--accent)" }}>
                        <FaShoppingBag />
                      </div>
                      <div>
                        <span className="text-muted" style={{ fontSize: "12.5px" }}>Total Orders</span>
                        <h4 className="fw-bold mb-0">{orders.length}</h4>
                      </div>
                    </div>
                  </div>

                  <div className="col-12 col-md-4">
                    <div className="p-4 bg-white rounded-3 border d-flex align-items-center gap-3" style={{ boxShadow: "var(--shadow-xs)" }}>
                      <div className="trust-feature-icon" style={{ background: "var(--danger-light)", color: "var(--danger)" }}>
                        <FaHeart />
                      </div>
                      <div>
                        <span className="text-muted" style={{ fontSize: "12.5px" }}>Wishlist Items</span>
                        <h4 className="fw-bold mb-0">{wishlistCount}</h4>
                      </div>
                    </div>
                  </div>

                  <div className="col-12 col-md-4">
                    <div className="p-4 bg-white rounded-3 border d-flex align-items-center gap-3" style={{ boxShadow: "var(--shadow-xs)" }}>
                      <div className="trust-feature-icon" style={{ background: "var(--success-light)", color: "var(--success)" }}>
                        <FaMapMarkerAlt />
                      </div>
                      <div>
                        <span className="text-muted" style={{ fontSize: "12.5px" }}>Saved Addresses</span>
                        <h4 className="fw-bold mb-0">{addresses.length}</h4>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Recent Activity Card */}
                <div className="cart-table-card p-4">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="fw-bold mb-0">Recent Orders</h5>
                    <button
                      type="button"
                      onClick={() => setActiveTab("orders")}
                      className="btn btn-link p-0 text-decoration-none"
                      style={{ fontSize: "13.5px", color: "var(--accent)" }}
                    >
                      View All Orders ({orders.length}) →
                    </button>
                  </div>

                  {orders.length === 0 ? (
                    <div className="text-center py-4 text-muted">
                      <p className="mb-2">You have not placed any orders yet.</p>
                      <Link to="/collection" className="btn btn-primary btn-sm rounded-pill px-3">
                        Start Shopping
                      </Link>
                    </div>
                  ) : (
                    <div className="d-flex flex-column gap-3">
                      {orders.slice(0, 3).map((ord) => {
                        const total = getOrderTotal(ord);
                        return (
                          <div key={ord.id} className="p-3 border rounded-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
                            <div>
                              <span className="fw-bold" style={{ fontSize: "14px" }}>
                                Order #ORD-{ord.id}
                              </span>
                              <div className="text-muted" style={{ fontSize: "12.5px" }}>
                                Placed on {ord.created_at ? new Date(ord.created_at).toLocaleDateString() : "Recent"} • {ord.payment_mode || "COD"}
                              </div>
                            </div>
                            <div className="text-end">
                              <div className="fw-bold" style={{ color: "var(--accent)", fontSize: "15px" }}>
                                Rs. {Number(total).toLocaleString()}
                              </div>
                              <span className="badge bg-success" style={{ fontSize: "11px" }}>
                                {ord.status_label || "Confirmed"}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* 2. Orders Tab - Full Details & Itemized Breakdown */}
            {activeTab === "orders" && (
              <div className="cart-table-card p-4">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <div>
                    <h5 className="fw-bold mb-1">My Orders ({orders.length})</h5>
                    <span className="text-muted" style={{ fontSize: "13px" }}>
                      Track shipments, view ordered products, amounts & invoices
                    </span>
                  </div>
                  <Link to="/collection" className="btn btn-primary btn-sm rounded-pill px-3" style={{ background: "var(--accent)", border: "none" }}>
                    + Shop More
                  </Link>
                </div>

                {loadingOrders ? (
                  <div className="d-flex flex-column gap-3">
                    {[1, 2].map((n) => (
                      <div key={n} className="skeleton-box" style={{ height: "140px", width: "100%" }}></div>
                    ))}
                  </div>
                ) : orders.length === 0 ? (
                  <div className="empty-state-modern py-5">
                    <div className="empty-state-icon">
                      <FaBoxOpen />
                    </div>
                    <h5 className="fw-bold mb-2">No Orders Found</h5>
                    <p className="text-muted mb-4">You haven't placed any orders with this account yet.</p>
                    <Link to="/collection" className="btn btn-primary rounded-pill px-4 py-2" style={{ background: "var(--accent)" }}>
                      Explore Collections
                    </Link>
                  </div>
                ) : (
                  <div className="d-flex flex-column gap-4">
                    {orders.map((ord) => {
                      const totalAmount = getOrderTotal(ord);
                      const items = ord.orderitems || [];
                      const isComplete = ord.order_complete === 1 || ord.status === 4;
                      const isShipped = isComplete || ord.status === 3;
                      const isProcessing = isShipped || ord.status === 2;

                      return (
                        <div
                          key={ord.id}
                          className="border rounded-4 bg-white overflow-hidden"
                          style={{ boxShadow: "var(--shadow-sm)" }}
                        >
                          {/* Order Header Summary Bar */}
                          <div className="p-3 bg-light border-bottom d-flex justify-content-between align-items-center flex-wrap gap-2">
                            <div>
                              <div className="d-flex align-items-center gap-2 mb-1">
                                <span className="fw-extrabold" style={{ fontSize: "16px", color: "var(--primary)" }}>
                                  Order #ORD-{ord.id}
                                </span>
                                {ord.tracking_no && (
                                  <span className="badge bg-secondary" style={{ fontSize: "11px" }}>
                                    Tracking: {ord.tracking_no}
                                  </span>
                                )}
                              </div>
                              <span className="text-muted" style={{ fontSize: "12.5px" }}>
                                Placed on {ord.created_at ? new Date(ord.created_at).toLocaleString() : "Recently"}
                              </span>
                            </div>

                            <div className="text-end">
                              <span className="text-muted" style={{ fontSize: "12px" }}>Total Amount:</span>
                              <div className="fw-bold" style={{ fontSize: "17px", color: "var(--accent)" }}>
                                Rs. {Number(totalAmount).toLocaleString()}
                              </div>
                              <span className="badge bg-primary mt-1" style={{ fontSize: "11px" }}>
                                {ord.payment_mode || "Cash on Delivery"}
                              </span>
                            </div>
                          </div>

                          {/* Itemized Products List */}
                          <div className="p-3 border-bottom">
                            <h6 className="fw-bold mb-3" style={{ fontSize: "13.5px" }}>
                              Ordered Items ({items.length})
                            </h6>
                            <div className="d-flex flex-column gap-3">
                              {items.map((item, itemIdx) => {
                                const prod = item.products;
                                const itemImg = prod?.product_image?.[0]?.image || prod?.image;
                                const itemPrice = item.price || item.productsprice?.seeling_price || 0;
                                const lineTotal = itemPrice * (item.qty || 1);

                                return (
                                  <div
                                    key={itemIdx}
                                    className="d-flex align-items-center justify-content-between gap-3 p-2 rounded-3 bg-light"
                                  >
                                    <div className="d-flex align-items-center gap-3">
                                      <div
                                        style={{
                                          width: "56px",
                                          height: "56px",
                                          borderRadius: "var(--radius-md)",
                                          overflow: "hidden",
                                          background: "#fff",
                                          border: "1px solid var(--border-color)",
                                          flexShrink: 0,
                                        }}
                                      >
                                        <img
                                          src={getProductImageUrl(itemImg)}
                                          alt={prod?.name || "Product"}
                                          style={{ width: "100%", height: "100%", objectFit: "contain", padding: "3px" }}
                                          onError={handleProductImageError}
                                        />
                                      </div>
                                      <div>
                                        <h6 className="fw-bold mb-0" style={{ fontSize: "14px" }}>
                                          {item.display_name || prod?.name || "Item"}
                                        </h6>
                                        <span className="text-muted" style={{ fontSize: "12.5px" }}>
                                          Rs. {Number(itemPrice).toLocaleString()} × {item.qty || 1}
                                        </span>
                                      </div>
                                    </div>

                                    <div className="text-end pe-2">
                                      <span className="fw-bold" style={{ fontSize: "14px" }}>
                                        Rs. {Number(lineTotal).toLocaleString()}
                                      </span>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>

                          {/* Shipping Information & Timeline */}
                          <div className="p-3 bg-white">
                            <div className="row g-3">
                              {/* Address */}
                              <div className="col-12 col-md-5 border-end-md">
                                <span className="text-muted fw-semibold" style={{ fontSize: "12px" }}>
                                  DELIVERY DETAILS
                                </span>
                                <p className="mb-1 fw-bold" style={{ fontSize: "13.5px" }}>
                                  {ord.firstname} {ord.lastname}
                                </p>
                                <p className="mb-1 text-muted" style={{ fontSize: "12.5px" }}>
                                  <FaMapMarkerAlt className="me-1 text-danger" />
                                  {ord.address}, {ord.city} {ord.state}
                                </p>
                                {ord.phone && (
                                  <span className="text-muted" style={{ fontSize: "12px" }}>
                                    <FaPhoneAlt className="me-1" /> {ord.phone}
                                  </span>
                                )}
                              </div>

                              {/* Timeline Tracker */}
                              <div className="col-12 col-md-7">
                                <span className="text-muted fw-semibold" style={{ fontSize: "12px" }}>
                                  SHIPMENT PROGRESS
                                </span>
                                <div className="d-flex align-items-center justify-content-between mt-2 pt-1 position-relative">
                                  <div className="text-center">
                                    <div className="p-2 rounded-circle bg-success text-white mx-auto mb-1" style={{ width: "28px", height: "28px", fontSize: "11px" }}>
                                      <FaCheckCircle />
                                    </div>
                                    <span style={{ fontSize: "11px", fontWeight: 600 }}>Placed</span>
                                  </div>
                                  <div className="text-center">
                                    <div className="p-2 rounded-circle bg-success text-white mx-auto mb-1" style={{ width: "28px", height: "28px", fontSize: "11px" }}>
                                      <FaCheckCircle />
                                    </div>
                                    <span style={{ fontSize: "11px", fontWeight: 600 }}>Confirmed</span>
                                  </div>
                                  <div className="text-center">
                                    <div className={`p-2 rounded-circle text-white mx-auto mb-1 ${isProcessing ? "bg-primary" : "bg-light text-muted"}`} style={{ width: "28px", height: "28px", fontSize: "11px" }}>
                                      <FaClock />
                                    </div>
                                    <span style={{ fontSize: "11px", fontWeight: 600, color: isProcessing ? "var(--text-main)" : "var(--text-muted)" }}>Processing</span>
                                  </div>
                                  <div className="text-center">
                                    <div className={`p-2 rounded-circle text-white mx-auto mb-1 ${isShipped ? "bg-success" : "bg-light text-muted"}`} style={{ width: "28px", height: "28px", fontSize: "11px" }}>
                                      <FaTruck />
                                    </div>
                                    <span style={{ fontSize: "11px", fontWeight: isShipped ? 600 : 500, color: isShipped ? "var(--text-main)" : "var(--text-muted)" }}>Shipped</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Order Footer Actions with Tax Invoice Button */}
                          <div className="p-3 bg-light border-top d-flex justify-content-between align-items-center flex-wrap gap-2">
                            <span className="text-muted" style={{ fontSize: "12.5px" }}>
                              A simple invoice is ready for this order
                            </span>
                            <div className="d-flex align-items-center gap-2">
                              {ord.tracking_no && (
                                <Link
                                  to={`/track-order?tracking=${ord.tracking_no}`}
                                  className="btn btn-sm btn-outline-secondary rounded-pill px-3 py-1.5 fw-semibold"
                                  style={{ fontSize: "12px" }}
                                >
                                  <FaTruck size={12} className="me-1" /> Track Delivery
                                </Link>
                              )}
                              <Link
                                to={`/order-invoice/${ord.id}`}
                                className="btn btn-sm btn-primary rounded-pill px-3.5 py-1.5 fw-bold d-flex align-items-center gap-1 shadow-sm"
                                style={{ fontSize: "12px" }}
                              >
                                <FaFileInvoiceDollar size={13} /> View invoice
                              </Link>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* 3. Address Book Tab */}
            {activeTab === "addresses" && (
              <div className="cart-table-card p-4">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h5 className="fw-bold mb-0">Saved Addresses</h5>
                  <button
                    type="button"
                    onClick={() => setShowAddAddress(!showAddAddress)}
                    className="btn btn-primary btn-sm rounded-pill d-flex align-items-center gap-1"
                    style={{ background: "var(--primary)", border: "none" }}
                  >
                    <FaPlus size={11} /> {showAddAddress ? "Cancel" : "Add Address"}
                  </button>
                </div>

                {/* Add Address Form */}
                {showAddAddress && (
                  <form onSubmit={handleSaveAddress} className="p-3 border rounded-3 mb-4 bg-light" noValidate>
                    {addressErrors.form && <div className="alert alert-danger py-2 px-3 small" role="alert">{addressErrors.form}</div>}
                    <h6 className="fw-bold mb-3">Add New Address</h6>
                    <div className="row g-3">
                      <div className="col-md-6">
                        <label className="form-label" style={{ fontSize: "13px" }}>Address Title</label>
                        <select
                          className="form-select"
                          value={newAddress.title}
                          onChange={(e) => setNewAddress({ ...newAddress, title: e.target.value })}
                        >
                          <option value="Home">Home</option>
                          <option value="Office">Office</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                      <div className="col-md-6">
                        <label className="form-label" style={{ fontSize: "13px" }}>Contact Person</label>
                        <input
                          type="text"
                          className={`form-control ${addressErrors.full_name ? "is-invalid" : ""}`}
                          value={newAddress.full_name}
                          onChange={(e) => setNewAddress({ ...newAddress, full_name: e.target.value })}
                          placeholder="e.g. Ali Raza"
                          required
                        />
                        {addressErrors.full_name && <div className="invalid-feedback d-block">{addressErrors.full_name}</div>}
                      </div>
                      <div className="col-md-6">
                        <label className="form-label" style={{ fontSize: "13px" }}>Phone Number</label>
                        <input
                          type="text"
                          className={`form-control ${addressErrors.phone ? "is-invalid" : ""}`}
                          value={newAddress.phone}
                          onChange={(e) => setNewAddress({ ...newAddress, phone: e.target.value })}
                          placeholder="0300-1234567"
                          required
                        />
                        {addressErrors.phone && <div className="invalid-feedback d-block">{addressErrors.phone}</div>}
                      </div>
                      <div className="col-md-6">
                        <label className="form-label" style={{ fontSize: "13px" }}>City</label>
                        <input
                          type="text"
                          className="form-control"
                          value={newAddress.city}
                          onChange={(e) => setNewAddress({ ...newAddress, city: e.target.value })}
                          placeholder="Karachi, Lahore..."
                          required
                        />
                      </div>
                      <div className="col-12">
                        <label className="form-label" style={{ fontSize: "13px" }}>Complete Street Address</label>
                        <textarea
                          className={`form-control ${addressErrors.address ? "is-invalid" : ""}`}
                          rows="2"
                          value={newAddress.address}
                          onChange={(e) => setNewAddress({ ...newAddress, address: e.target.value })}
                          placeholder="House/Apartment #, Street, Area"
                          required
                        />
                        {addressErrors.address && <div className="invalid-feedback d-block">{addressErrors.address}</div>}
                      </div>
                    </div>
                    <button
                      type="submit"
                      className="btn btn-dark btn-sm rounded-pill mt-3 px-4"
                      disabled={savingAddress}
                    >
                      {savingAddress ? "Saving..." : "Save Address"}
                    </button>
                  </form>
                )}

                {/* Addresses Grid */}
                {loadingAddresses ? (
                  <div className="skeleton-box" style={{ height: "100px", width: "100%" }}></div>
                ) : addresses.length === 0 ? (
                  <p className="text-muted">No saved addresses found. Click "+ Add Address" to create one.</p>
                ) : (
                  <div className="row g-3">
                    {addresses.map((addr) => (
                      <div className="col-12 col-md-6" key={addr.id}>
                        <div className="p-3 border rounded-3 position-relative bg-white" style={{ boxShadow: "var(--shadow-xs)" }}>
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <span className="badge bg-dark">{addr.title}</span>
                            <button
                              type="button"
                              onClick={() => handleDeleteAddress(addr.id)}
                              className="btn btn-link text-danger p-0"
                              title="Delete Address"
                            >
                              <FaTrashAlt size={12} />
                            </button>
                          </div>
                          <h6 className="fw-bold mb-1">{addr.full_name}</h6>
                          <p className="text-muted mb-1" style={{ fontSize: "13px" }}>{addr.address}, {addr.city}</p>
                          <span className="text-muted" style={{ fontSize: "12.5px" }}>📞 {addr.phone}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* 4. Settings Tab */}
            {activeTab === "settings" && (
              <div className="cart-table-card p-4">
                <h5 className="fw-bold mb-4">Password & Security Settings</h5>

                <form onSubmit={handleChangePassword} style={{ maxWidth: "450px" }} noValidate>
                  {passwordErrors.form && <div className="alert alert-danger py-2 px-3 small" role="alert">{passwordErrors.form}</div>}
                  <div className="mb-3">
                    <label className="form-label fw-semibold" style={{ fontSize: "13.5px" }}>
                      Current Password
                    </label>
                    <input
                      type="password"
                      className={`form-control ${passwordErrors.currentPassword ? "is-invalid" : ""}`}
                      value={passwordInput.currentPassword}
                      onChange={(e) => setPasswordInput({ ...passwordInput, currentPassword: e.target.value })}
                      placeholder="Enter current password"
                    />
                    {passwordErrors.currentPassword && <div className="invalid-feedback d-block">{passwordErrors.currentPassword}</div>}
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-semibold" style={{ fontSize: "13.5px" }}>
                      New Password
                    </label>
                    <input
                      type="password"
                      className={`form-control ${passwordErrors.newPassword ? "is-invalid" : ""}`}
                      value={passwordInput.newPassword}
                      onChange={(e) => setPasswordInput({ ...passwordInput, newPassword: e.target.value })}
                      placeholder="At least 8 characters"
                      required
                    />
                    {passwordErrors.newPassword && <div className="invalid-feedback d-block">{passwordErrors.newPassword}</div>}
                  </div>

                  <div className="mb-4">
                    <label className="form-label fw-semibold" style={{ fontSize: "13.5px" }}>
                      Confirm New Password
                    </label>
                    <input
                      type="password"
                      className={`form-control ${passwordErrors.confirmPassword ? "is-invalid" : ""}`}
                      value={passwordInput.confirmPassword}
                      onChange={(e) => setPasswordInput({ ...passwordInput, confirmPassword: e.target.value })}
                      placeholder="Re-enter new password"
                      required
                    />
                    {passwordErrors.confirmPassword && <div className="invalid-feedback d-block">{passwordErrors.confirmPassword}</div>}
                  </div>

                  <button
                    type="submit"
                    className="btn btn-primary rounded-pill px-4 py-2 fw-bold"
                    style={{ background: "var(--primary)", border: "none" }}
                    disabled={updatingPassword}
                  >
                    {updatingPassword ? "Updating..." : "Update Password"}
                  </button>
                </form>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
